import { useRouter, type RouteLocationRaw } from 'vue-router'
import type { NavigationMenuItem } from '../components/singleton/SHeader/types'

export function useNavigation() {
  const router = useRouter()

  /**
   * Validates that a route object can be resolved without relying on
   * params inherited from the current route. Returns the route if valid,
   * or `undefined` if the route requires params we didn't explicitly provide.
   */
  function resolveIfValid(to: RouteLocationRaw): RouteLocationRaw | undefined {
    try {
      const resolved = router.resolve(to)
      const providedParams = typeof to === 'object' && 'params' in to ? (to.params ?? {}) : {}
      for (const key of Object.keys(resolved.params)) {
        if (!(key in providedParams)) return undefined
      }
      return to
    } catch {
      return undefined
    }
  }

  function getMenuItemTo(item: NavigationMenuItem): RouteLocationRaw | undefined {
    if (item.locale_name === 'common.other' || item.locale_name === 'common.finance') {
      return undefined
    }

    if (item.sub_menu != null && item.sub_menu.length > 0) {
      return getMenuItemTo(item.sub_menu[0])
    }

    if (item.name != null && router.hasRoute(item.name)) {
      return resolveIfValid({ name: item.name, params: item.params ?? {} })
    }

    if (item.params == null) {
      return undefined
    }

    const parent = item.params.parent
    const resource = item.params.resource

    if (resource != null) {
      return {
        name: 'ItemSearch',
        params: { parent: parent ?? resource, resource },
      }
    }

    return undefined
  }

  /**
   * Imperatively navigates to a menu item's route.
   * Prefer using `getMenuItemTo()` with `:to` bindings instead.
   * This is only needed for programmatic navigation (e.g. after async search).
   */
  async function navigateToItem(item: NavigationMenuItem) {
    const to = getMenuItemTo(item)
    if (to != null) {
      await router.push(to)
    }
  }

  return {
    getMenuItemTo,
    navigateToItem,
  }
}
